<script setup lang="ts">
import { ref, computed, h } from 'vue'
import {
  ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuPortal,
  ContextMenuContent,
  ContextMenuLabel,
  ContextMenuItem,
} from 'radix-vue'
import type { ColumnDef } from '@tanstack/vue-table'
import type { RosterMember, User } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosChip from '@/components/ui/FosChip.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTable from '@/components/ui/FosTable.vue'

// One list of everyone in the agency: FoS accounts and D4H roster members,
// joined on roster.user_id (set by the D4H sync when emails match).
const props = defineProps<{
  users: User[]
  roster: RosterMember[]
  // Emails with an unclaimed invite, so D4H-only rows show "Invited" instead of "Invite"
  invitedEmails: string[]
  d4hWebUrl: string
  isAdmin: boolean
  currentUserId: string
}>()

const emit = defineEmits<{
  'change-role': [user: User, role: string]
  deactivate: [user: User]
  reactivate: [user: User]
  invite: [email: string]
}>()

interface MemberRow {
  key: string
  first: string
  last: string
  nickname: string
  email: string
  phone: string
  user: User | null
  roster: RosterMember | null
}

type Filter = 'all' | 'accounts' | 'no_account' | 'not_in_d4h'

const search = ref('')
const filter = ref<Filter>('all')

// Row the admin context menu was opened on
const contextRow = ref<MemberRow | null>(null)

const rows = computed<MemberRow[]>(() => {
  const rosterByUserId = new Map(props.roster.filter((r) => r.user_id).map((r) => [r.user_id!, r]))
  // Unlinked roster records also match on email, so someone who signed up since the
  // last D4H sync shows as one row now. The next sync sets user_id for the same match.
  const unlinkedByEmail = new Map(props.roster.filter((r) => !r.user_id && r.email).map((r) => [r.email, r]))
  const matched = new Set<string>()
  const result: MemberRow[] = props.users.map((u) => {
    const r = rosterByUserId.get(u.user_id) ?? unlinkedByEmail.get((u.email ?? '').toLowerCase()) ?? null
    if (r) matched.add(r.roster_id)
    return {
      key: `user:${u.user_id}`,
      first: u.user_first_name,
      last: u.user_last_name,
      nickname: r?.nickname ?? '',
      email: u.email,
      phone: u.phone || r?.phone || '',
      user: u,
      roster: r,
    }
  })
  for (const r of props.roster) {
    // A roster member linked to a user outside this list (e.g. moved agency) still shows as D4H-only
    if (matched.has(r.roster_id)) continue
    result.push({
      key: `d4h:${r.roster_id}`,
      first: r.first,
      last: r.last,
      nickname: r.nickname ?? '',
      email: r.email,
      phone: r.phone,
      user: null,
      roster: r,
    })
  }
  return result.sort((a, b) =>
    (a.last || a.first).localeCompare(b.last || b.first) || a.first.localeCompare(b.first),
  )
})

const hasRoster = computed(() => props.roster.length > 0)

const counts = computed(() => ({
  all: rows.value.length,
  accounts: rows.value.filter((r) => r.user).length,
  no_account: rows.value.filter((r) => !r.user).length,
  not_in_d4h: rows.value.filter((r) => !r.roster).length,
}))

const filters = computed<{ key: Filter; label: string }[]>(() => [
  { key: 'all', label: 'All' },
  { key: 'accounts', label: 'FoS Accounts' },
  // Only meaningful once a D4H roster exists
  ...(hasRoster.value
    ? [
        { key: 'no_account' as const, label: 'No Account' },
        { key: 'not_in_d4h' as const, label: 'Not in D4H' },
      ]
    : []),
])

const visibleRows = computed(() => {
  const q = search.value.trim().toLowerCase()
  return rows.value.filter((r) => {
    if (q) {
      const haystack = `${r.first} ${r.last} ${r.nickname} ${r.email} ${r.phone}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }
    switch (filter.value) {
      case 'accounts': return r.user !== null
      case 'no_account': return r.user === null
      case 'not_in_d4h': return r.roster === null
      default: return true
    }
  })
})

const invited = computed(() => new Set(props.invitedEmails.map((e) => e.toLowerCase())))

function displayName(r: MemberRow): string {
  return [r.first, r.nickname ? `"${r.nickname}"` : '', r.last].filter(Boolean).join(' ')
}

function d4hProfileUrl(r: RosterMember): string | undefined {
  return props.d4hWebUrl ? `${props.d4hWebUrl}/team/members/view/${r.external_id}` : undefined
}

function qualSummary(r: RosterMember): string {
  return [r.medical_qual, r.searcher_type, r.rope_qual, ...r.misc_quals]
    .filter((q) => q && q !== 'None')
    .join(', ')
}

// Compact name tags (smaller than FosChip). The icon size is inline because the Material
// Symbols stylesheet sets font-size outside Tailwind's layers, which beats text-[..] utilities.
const TAG_CLASS = 'inline-flex items-center gap-0.5 px-1 py-px font-mono text-[0.6rem] leading-none uppercase tracking-wide font-bold rounded shrink-0'
const D4H_TAG_CLASS = `${TAG_CLASS} bg-secondary-container text-secondary`
const ADMIN_TAG_CLASS = `${TAG_CLASS} bg-surface-container-highest text-primary`
const MENU_ITEM_CLASS = 'px-3 py-1.5 font-mono text-xs text-on-surface cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest data-[disabled]:text-outline data-[disabled]:cursor-not-allowed'

function d4hChip(r: RosterMember) {
  const url = d4hProfileUrl(r)
  if (!url) return h('span', { class: D4H_TAG_CLASS, title: 'Set the D4H web address to link to the profile' }, 'D4H')
  return h('a', {
    href: url,
    target: '_blank',
    rel: 'noopener noreferrer',
    class: `${D4H_TAG_CLASS} hover:opacity-80`,
    title: 'Open D4H profile',
  }, [
    'D4H',
    h('span', { class: 'material-symbols-outlined', style: 'font-size: 10px' }, 'open_in_new'),
  ])
}

const columns = computed<ColumnDef<MemberRow>[]>(() => [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (r) => `${r.last} ${r.first}`.trim(),
    enableSorting: true,
    cell: ({ row }) => h('div', { class: 'flex items-center gap-2' }, [
      h('span', { class: 'text-sm text-on-surface' }, displayName(row.original)),
      row.original.user?.role === 'admin' ? h('span', { class: ADMIN_TAG_CLASS }, 'Admin') : null,
      row.original.roster ? d4hChip(row.original.roster) : null,
    ]),
  },
  {
    id: 'contact',
    header: 'Contact',
    accessorFn: (r) => r.email,
    cell: ({ row }) => h('div', { class: 'flex flex-col gap-0.5 font-mono' }, [
      h('span', { class: 'text-on-surface-variant' }, row.original.email),
      row.original.phone ? h('span', { class: 'text-outline' }, row.original.phone) : null,
    ]),
  },
  ...(hasRoster.value
    ? [{
        id: 'quals',
        header: 'Qualifications',
        cell: ({ row }) => row.original.roster ? qualSummary(row.original.roster) : '',
      } satisfies ColumnDef<MemberRow>]
    : []),
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { user, roster } = row.original
      const chips = []
      if (user) {
        chips.push(user.is_active === false
          ? h(FosChip, { variant: 'error' }, () => 'Inactive')
          : h(FosChip, { variant: 'success' }, () => 'Active'))
      } else if (invited.value.has(row.original.email.toLowerCase())) {
        chips.push(h(FosChip, { variant: 'info' }, () => 'Invited'))
      } else {
        chips.push(h(FosChip, { variant: 'default' }, () => 'No Account'))
      }
      if (roster) {
        chips.push(h(FosChip, { variant: roster.operational ? 'success' : 'warning' },
          () => roster.operational ? 'Operational' : 'Non-Op'))
      }
      return h('div', { class: 'flex flex-wrap gap-1' }, chips)
    },
  },
  ...(props.isAdmin
    ? [{
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const { user, email } = row.original
          if (user) {
            if (user.user_id === props.currentUserId) return null
            return user.is_active === false
              ? h(FosButton, { variant: 'tertiary', size: 'sm', onClick: () => emit('reactivate', user) }, () => 'Reactivate')
              : h(FosButton, { variant: 'tertiary', size: 'sm', class: 'text-error', onClick: () => emit('deactivate', user) }, () => 'Deactivate')
          }
          if (!email || invited.value.has(email.toLowerCase())) return null
          return h(FosButton, { variant: 'tertiary', size: 'sm', onClick: () => emit('invite', email) }, () => 'Invite')
        },
      } satisfies ColumnDef<MemberRow>]
    : []),
])
// Admins get a context menu on every row. Non-admins, and clicks outside a row
// (header, pager), get the browser's normal menu.
function onRowContextmenu(row: MemberRow) {
  contextRow.value = props.isAdmin ? row : null
}

// Runs after the row handler and before the menu trigger, so the menu only opens on a row
function guardContextmenu(event: MouseEvent) {
  if (!contextRow.value) event.stopPropagation()
}

const contextIsSelf = computed(() => contextRow.value?.user?.user_id === props.currentUserId)
const contextCanInvite = computed(() => {
  const row = contextRow.value
  return !!row && !row.user && !!row.email && !invited.value.has(row.email.toLowerCase())
})
const contextD4hUrl = computed(() => contextRow.value?.roster ? d4hProfileUrl(contextRow.value.roster) : undefined)

function inviteContextRow() {
  if (contextRow.value?.email) emit('invite', contextRow.value.email)
}

function openContextD4h() {
  if (contextD4hUrl.value) window.open(contextD4hUrl.value, '_blank', 'noopener,noreferrer')
}

function onMenuOpenChange(open: boolean) {
  if (!open) contextRow.value = null
}

function toggleAdmin() {
  const user = contextRow.value?.user
  if (user) emit('change-role', user, user.role === 'admin' ? 'member' : 'admin')
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-2">
      <div class="flex items-baseline gap-2 mr-auto">
        <label class="text-[10px] font-label uppercase tracking-widest text-outline">Members</label>
        <span v-if="isAdmin" class="text-xs text-outline">Right-click a member for options</span>
      </div>
      <div class="w-64">
        <FosInput v-model="search" placeholder="Search name, email or phone" />
      </div>
      <div class="flex border border-border rounded overflow-hidden" role="group" aria-label="Filter members">
        <button
          v-for="f in filters"
          :key="f.key"
          type="button"
          :aria-pressed="filter === f.key"
          :class="[
            'px-2.5 py-2 text-[10px] font-label uppercase tracking-widest border-r border-border last:border-0 transition-colors',
            filter === f.key
              ? 'bg-primary text-on-primary'
              : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface',
          ]"
          @click="filter = f.key"
        >
          {{ f.label }} <span class="font-mono">{{ counts[f.key] }}</span>
        </button>
      </div>
    </div>

    <ContextMenuRoot :modal="false" @update:open="onMenuOpenChange">
      <ContextMenuTrigger as-child>
        <div class="border border-border rounded overflow-hidden">
          <!-- Inner element so stopPropagation keeps the event from the trigger's own listener -->
          <div @contextmenu="guardContextmenu">
            <FosTable
              :columns="columns"
              :data="visibleRows"
              :page-size="25"
              :on-row-contextmenu="onRowContextmenu"
            />
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuPortal>
        <ContextMenuContent class="z-50 min-w-48 bg-surface-container-high border border-border rounded shadow-xl py-1">
          <ContextMenuLabel class="px-3 py-1.5 text-[10px] font-label uppercase tracking-widest text-outline truncate">
            {{ contextRow ? displayName(contextRow) : '' }}
          </ContextMenuLabel>
          <template v-if="contextRow?.user">
            <ContextMenuItem
              :disabled="contextIsSelf"
              :class="MENU_ITEM_CLASS"
              @select="toggleAdmin"
            >
              {{ contextRow.user.role === 'admin' ? 'Remove admin' : 'Make admin' }}
            </ContextMenuItem>
            <p v-if="contextIsSelf" class="px-3 pb-1.5 text-[11px] text-outline">You can't change your own role</p>
          </template>
          <ContextMenuItem
            v-else
            :disabled="!contextCanInvite"
            :class="MENU_ITEM_CLASS"
            @select="inviteContextRow"
          >
            {{ contextCanInvite ? 'Invite to First on Scene' : 'Invite sent' }}
          </ContextMenuItem>
          <ContextMenuItem
            v-if="contextD4hUrl"
            :class="MENU_ITEM_CLASS"
            @select="openContextD4h"
          >
            Open D4H profile
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenuPortal>
    </ContextMenuRoot>
  </div>
</template>
